import { useState, useCallback } from "react";
import { 
  CreditCard, 
  AlertTriangle, 
  FileText, 
  Smartphone, 
  CheckCircle, 
  DollarSign, 
  Shield, 
  Network,
  TrendingUp,
  Users,
  BarChart3,
  Zap,
  Lock,
  MapPin,
  Tag,
  Settings,
  RefreshCw,
  Database,
  Search,
  AlertCircle
} from "lucide-react";
import Header from "./header";
import Footer from "./footer";

export default function AcquirerServicesPage() {
  const [flippedCards, setFlippedCards] = useState({});

  const flipCardServices = [
    { 
      title: "Tag Detail System", 
      icon: Tag, 
      color: "from-blue-500 to-blue-600",
      frontImage: "https://images.unsplash.com/photo-1441986300917-64674bd600d8?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&h=300&q=80",
      description: "Comprehensive tag management and tracking system for efficient toll operations with real-time monitoring and analytics."
    },
    { 
      title: "Pass Creation", 
      icon: CreditCard, 
      color: "from-emerald-500 to-emerald-600",
      frontImage: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=300&fit=crop",
      description: "Streamlined pass generation system with automated validation, processing and secure credential management."
    },
    { 
      title: "Transaction Processing", 
      icon: RefreshCw, 
      color: "from-purple-500 to-purple-600",
      frontImage: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=400&h=300&fit=crop",
      description: "Real-time transaction handling with secure payment gateway integration and advanced fraud detection systems."
    },
    { 
      title: "Recon Management", 
      icon: Database, 
      color: "from-orange-500 to-orange-600",
      frontImage: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=300&fit=crop",
      description: "Advanced reconciliation system for accurate financial reporting, audit trails and compliance management."
    },
{
  title: "BLT",
  icon: Settings,
  color: "from-red-500 to-red-600",
  frontImage: "https://images.unsplash.com/photo-1518186285589-2f7649de83e0?w=400&h=300&fit=crop",
  description: "Blacklist Tag module helps identify, flag, and manage entities or transactions that pose potential risks or violate compliance policies."
},
    { 
      title: "Request Tag Detail", 
      icon: Search, 
      color: "from-teal-500 to-teal-600",
      frontImage: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&h=300&fit=crop",
      description: "Instant tag information retrieval system with comprehensive data access and detailed reporting capabilities."
    },
    { 
      title: "Violation Management", 
      icon: AlertCircle, 
      color: "from-red-500 to-red-600",
      frontImage: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=400&h=300&fit=crop",
      description: "Automated violation detection and management system with penalty processing and dispute resolution."
    },
    { 
      title: "Analytics Dashboard", 
      icon: BarChart3, 
      color: "from-cyan-500 to-cyan-600",
      frontImage: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=300&fit=crop",
      description: "Comprehensive analytics and reporting dashboard with real-time insights and performance metrics."
    }
  ];

  const stats = [
    { label: "Active Palaza", value: "150+", icon: Users },
    { label: "Daily Transactions", value: "120M+", icon: BarChart3 },
    { label: "Success Rate", value: "99.9%", icon: TrendingUp },
    { label: "Average Response", value: "<900ms", icon: Zap }
  ];

  const handleCardHover = useCallback((index, isHovered) => {
    setFlippedCards(prev => ({
      ...prev,
      [index]: isHovered
    }));
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header/>
      <style jsx>{`
        .flip-card {
          perspective: 1000px;
        }
        
        .flip-card-inner {
          position: relative;
          width: 100%;
          height: 100%;
          text-align: center;
          transition: transform 0.6s ease-in-out;
          transform-style: preserve-3d;
        }
        
        .flip-card.flipped .flip-card-inner {
          transform: rotateY(180deg);
        }
        
        .flip-card-front, .flip-card-back {
          position: absolute;
          width: 100%;
          height: 100%;
          backface-visibility: hidden;
          border-radius: 1rem;
          overflow: hidden;
        }
        
        .flip-card-back {
          transform: rotateY(180deg);
        }
        
        .hero-bg {
          background-image: url("./tolaplaza.jpg");
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
        }

        .card-title-bg {
          background: linear-gradient(135deg, rgba(30, 58, 138, 0.95) 0%, rgba(59, 130, 246, 0.95) 100%);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(255, 255, 255, 0.2);
        }

        .floating-animation {
          animation: float 6s ease-in-out infinite;
        }

        .floating-animation:nth-child(2) {
          animation-delay: -2s;
        }

        .floating-animation:nth-child(3) {
          animation-delay: -4s;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }

        .gradient-text {
          background: linear-gradient(135deg, #60a5fa, #3b82f6);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .light-gradient-text {
          background: linear-gradient(135deg, #1e40af, #3b82f6);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }
      `}</style>

      {/* Section 1: Dark Hero Section */}
      <section className="relative py-18 overflow-hidden min-h-[80vh] flex items-center bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900">
        <div className="absolute inset-0 hero-bg opacity-20"></div>

        {/* Floating Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-20 w-64 h-64 bg-blue-500/20 rounded-full blur-3xl floating-animation"></div>
          <div className="absolute bottom-20 right-20 w-80 h-80 bg-purple-500/20 rounded-full blur-3xl floating-animation"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-indigo-500/15 rounded-full blur-3xl floating-animation"></div>
        </div>
        
        <div className="relative z-10 px-6 max-w-7xl mx-auto w-full">
          <div className="text-center">
            <div className="inline-flex items-center space-x-2 bg-blue-600/90 backdrop-blur-sm text-white px-6 py-3 rounded-full text-sm font-medium mb-8 shadow-xl border border-blue-400/30">
              <Lock className="w-4 h-4" />
              <span>India's Leading Technology Services Provider</span>
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-8 leading-tight">
              Acquirer Solutions
              <span className="block gradient-text">
                Revolution
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-200 mb-12 leading-relaxed max-w-4xl mx-auto">
              From FasTag to fintech, we are a leading NETC technology provider, 
              transforming tolling and payments for a smarter and more digital India.
            </p>
          </div>
        </div>
      </section>

      {/* Section 2: Light Services Section */}
      <section className="py-24 px-6 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-20">
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-8">
              Our Key Features
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              Comprehensive suite of payment processing and management solutions 
              designed for modern financial institutions and merchants
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {flipCardServices.map((service, index) => (
              <div
                key={index}
                className={`flip-card h-96 cursor-pointer transform transition-transform duration-300 hover:scale-105 ${flippedCards[index] ? 'flipped' : ''}`}
                onMouseEnter={() => handleCardHover(index, true)}
                onMouseLeave={() => handleCardHover(index, false)}
              >
                <div className="flip-card-inner">
                  <div className="flip-card-front shadow-xl border border-gray-200">
                    <div 
                      className="relative w-full h-full bg-cover bg-center flex flex-col justify-end"
                      style={{
                        backgroundImage: `url("${service.frontImage}")`
                      }}
                    >
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
                      
                      <div className="relative z-10 card-title-bg m-4 p-6 rounded-xl">
                        <div className="flex items-center justify-center mb-3">
                          <div className="w-12 h-12 bg-white/90 rounded-full flex items-center justify-center backdrop-blur-sm shadow-lg">
                            <service.icon className="w-6 h-6 text-blue-600" />
                          </div>
                        </div>
                        <h3 className="text-lg font-bold text-white text-center leading-tight mb-2">
                          {service.title.toUpperCase()}
                        </h3>
                        <div className="text-blue-200 text-xs text-center opacity-90 font-medium">
                          Hover to explore →
                        </div>
                      </div>
                      
                      <div className="absolute top-4 right-4 w-8 h-8 border-2 border-white/40 rounded-full"></div>
                      <div className="absolute top-4 left-4 w-6 h-6 border-2 border-white/30 rounded-full"></div>
                    </div>
                  </div>

                  <div className="flip-card-back shadow-xl">
                    <div className="relative w-full h-full bg-gradient-to-br from-slate-800 to-slate-900 p-6 flex flex-col justify-center">
                      <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-10`}></div>
                      <div className="relative z-10 text-center">
                        <div className={`w-16 h-16 rounded-full bg-gradient-to-r ${service.color} flex items-center justify-center mx-auto mb-4 shadow-lg`}>
                          <service.icon className="w-8 h-8 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-white mb-4">
                          {service.title}
                        </h3>
                        <p className="text-gray-300 text-sm leading-relaxed mb-4">
                          {service.description}
                        </p>
                        <div className="text-blue-300 text-xs font-medium">
                          ← Hover away to return
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 3: Dark Stats Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-slate-900 via-slate-800 to-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">Platform Performance</h2>
            <p className="text-lg text-gray-300">Trusted by thousands of businesses worldwide</p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="flex flex-col items-center space-y-4">
                  <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-600/20 to-blue-500/30 border border-blue-400/30 flex items-center justify-center group-hover:from-blue-500/30 group-hover:to-blue-400/40 transition-all duration-300 shadow-lg group-hover:shadow-xl backdrop-blur-sm">
                    <stat.icon className="w-10 h-10 text-blue-400" />
                  </div>
                  <div className="text-4xl font-bold text-white group-hover:text-blue-400 transition-colors duration-300">{stat.value}</div>
                  <div className="text-gray-300 font-medium">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 4: Light CTA Section */}
     

    
 <section className="py-24 px-6 bg-gradient-to-br from-white to-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-8">
              Ready to Transform Your
              <span className="block light-gradient-text">Payment Operations?</span>
            </h2>
            <p className="text-xl text-gray-600 mb-12 leading-relaxed">
              Join thousands of businesses that trust our platform for their payment processing needs. 
              Get started today and experience the difference.
            </p>
       <div className="flex flex-col sm:flex-row gap-4 justify-center">
  <a href="/Contact">
    <button className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-8 py-4 rounded-full text-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105">
      Get Started Free
    </button>
  </a>

  <a href="/Contact">
    <button className="border-2 border-gray-900 text-gray-900 px-8 py-4 rounded-full text-lg font-semibold hover:bg-gray-900 hover:text-white transition-all duration-300 shadow-lg hover:shadow-xl">
      Schedule Demo
    </button>
  </a>
</div>

          </div>
        </div>
      </section>
      <Footer/>
    </div>
  );
}